import React from 'react';
import { Calendar, MapPin, Users } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '../ui/button';
import GoogleMapView from '../GoogleMapView';
import FavoriteButton from '../FavoriteButton';
import ShareButton from '../ShareButton';
import { PAYMENTS_ENABLED } from '../../config/features';

const PriceBlock = ({ event }) => {
  const now = new Date();
  const presaleActive = event.presale_price != null && event.presale_deadline && new Date(event.presale_deadline) > now;
  const displayPrice = presaleActive ? event.presale_price : event.price;
  return (
    <>
      <div className="flex items-center justify-between">
        <span className="text-[#737373]">{presaleActive ? 'Preventa' : 'Precio'}</span>
        <span className="text-2xl font-bold text-[#0A0A0A]" data-testid="event-price">
          {displayPrice === 0 ? 'Gratis' : `${event.currency} ${displayPrice.toLocaleString('es-AR')}`}
        </span>
      </div>
      {presaleActive && (
        <p className="text-xs text-primary" data-testid="presale-notice">
          🔥 Precio de preventa hasta el {format(new Date(event.presale_deadline), 'dd/MM/yyyy', { locale: es })}. Luego: {event.currency} {event.price.toLocaleString('es-AR')}
        </p>
      )}
      {event.price_tiers && event.price_tiers.length > 0 && (
        <div className="pt-2 border-t border-[#E5E5E5] space-y-1" data-testid="price-tiers">
          <p className="text-xs text-[#737373] font-semibold">Otros precios</p>
          {event.price_tiers.map((t) => (
            <div key={`${t.name}-${t.price}`} className="flex justify-between text-sm">
              <span className="text-[#525252]">
                {t.name}{t.description ? ` · ${t.description}` : ''}
              </span>
              <span className="text-[#0A0A0A] font-medium">
                {t.price === 0 ? 'Gratis' : `${event.currency} ${t.price.toLocaleString('es-AR')}`}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const EventSidebar = ({ event, rsvpLoading, onRSVP }) => {
  const isPaid = event.price > 0;
  const paymentsHidden = isPaid && !PAYMENTS_ENABLED;

  return (
  <div className="bg-white/50 backdrop-blur-sm border border-[#E5E5E5] rounded-lg p-6 space-y-4 sticky top-24">
    <div className="space-y-3">
      {paymentsHidden ? (
        <div className="p-3 bg-[#F5F5F5] border border-[#E5E5E5] rounded-lg text-sm text-[#525252]" data-testid="tickets-coming-soon">
          🎟️ Venta de entradas próximamente
        </div>
      ) : (
        <PriceBlock event={event} />
      )}
      <div className="flex items-center justify-between text-sm">
        <span className="text-[#737373]">Capacidad</span>
        <span className="text-[#525252]">
          {event.remaining_capacity} / {event.capacity} disponibles
        </span>
      </div>
    </div>

    {!paymentsHidden && (
      <Button
        onClick={onRSVP}
        disabled={rsvpLoading || event.remaining_capacity <= 0}
        className="w-full bg-primary hover:bg-primary/90 text-white font-semibold"
        data-testid="rsvp-button"
      >
        {rsvpLoading
          ? 'Procesando...'
          : event.remaining_capacity <= 0
          ? 'Agotado'
          : isPaid
          ? `Pagar con MercadoPago — ${event.currency} ${event.price.toLocaleString('es-AR')}`
          : 'Anotarme gratis'}
      </Button>
    )}

    <div className="pt-4 border-t border-[#E5E5E5] space-y-3">
      <div className="flex items-start gap-3">
        <Calendar className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
        <div>
          <p className="text-sm text-[#737373]">Fecha y hora</p>
          <p className="text-[#0A0A0A] font-medium" data-testid="event-date">
            {format(new Date(event.start_date), "EEEE d 'de' MMMM, yyyy", { locale: es })}
          </p>
          <p className="text-[#525252] text-sm">
            {format(new Date(event.start_date), 'HH:mm', { locale: es })} hs
          </p>
        </div>
      </div>

      {event.location && (
        <div className="flex items-start gap-3">
          <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
          <div className="flex-1">
            <p className="text-sm text-[#737373]">Ubicación</p>
            {event.location.venue_name && (
              <p className="text-[#0A0A0A] font-semibold" data-testid="event-venue">
                {event.location.venue_name}
              </p>
            )}
            <p className="text-[#0A0A0A] font-medium" data-testid="event-location">
              {event.location.address}
            </p>
            <p className="text-[#525252] text-sm">{event.location.city}</p>
          </div>
        </div>
      )}

      {event.location && event.location.lat && event.location.lng && (
        <div className="mt-2" data-testid="event-map-wrapper">
          <GoogleMapView
            markers={[{
              id: event.event_id,
              lat: event.location.lat,
              lng: event.location.lng,
              title: event.title,
            }]}
            center={{ lat: event.location.lat, lng: event.location.lng }}
            zoom={15}
            height="220px"
          />
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${event.location.lat},${event.location.lng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-2 text-sm text-primary hover:underline"
            data-testid="event-directions-link"
          >
            Cómo llegar →
          </a>
        </div>
      )}

      <div className="flex items-start gap-3">
        <Users className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
        <div>
          <p className="text-sm text-[#737373]">Organizador</p>
          <p className="text-[#0A0A0A] font-medium">{event.organizer_name}</p>
        </div>
      </div>
    </div>

    <div className="grid grid-cols-2 gap-2">
      <div className="w-full">
        <FavoriteButton eventId={event.event_id} variant="inline" size={18} />
      </div>
      <ShareButton title={event.title} />
    </div>
  </div>
  );
};

export default EventSidebar;
